// Mirrors ai-qb-platform-demo TaxonomyTree shape: Subject → Scope (area) →
// Unit → Category, with `description` on category acting as the evaluation
// indicator (demo's `{ indicator, category_id }` leaf metadata).
// The previous 5-layer model (with an extra `item` leaf) was a panel-only
// invention and is being removed here so the taxonomy editor exposes the
// canonical four levels only.
import { chineseTaxonomySubject } from './chineseTaxonomy.mock';

export type TaxonomyLevel = 'subject' | 'area' | 'unit' | 'category';

export type TaxonomyNode = {
  id: string;
  level: TaxonomyLevel;
  name: string;
  code: string;
  description: string;
  children: TaxonomyNode[];
};

export type TaxonomySubject = TaxonomyNode & {
  level: 'subject';
};

export type ObjectiveCascade = Record<
  string,
  Record<string, Record<string, Record<string, string>>>
>;

// Sourced from env only — no hardcoded fallback. Mock taxonomy is opt-in.
// For real backend, taxonomy will move to Henry's `get_tree_list` (Stream E
// pending followup #1).
const API_BASE_URL = (process.env.REACT_APP_EDITORIAL_QUESTION_MOCK_API_URL || '').replace(
  /\/$/,
  ''
);
const TAXONOMY_SUBJECTS_URL = `${API_BASE_URL}/taxonomySubjects`;

const defaultTaxonomySubjects: TaxonomySubject[] = [
  {
    id: 'subject-math',
    level: 'subject',
    name: 'math',
    code: 'MATH',
    description: '幼兒數學學習目標分類架構。',
    children: [
      {
        id: 'area-daily',
        level: 'area',
        name: '日常生活應用',
        code: 'DAILY',
        description: '生活情境中的觀察、分類與辨識。',
        children: [
          {
            id: 'unit-life-measure',
            level: 'unit',
            name: '生活量度',
            code: 'DAILY-01',
            description: '以日常物件理解量度概念。',
            children: [
              {
                id: 'category-compare',
                level: 'category',
                name: '比較',
                code: '17',
                description: '能比較日常物件的長短、高矮、大小或重量。',
                children: []
              }
            ]
          }
        ]
      },
      {
        id: 'area-number',
        level: 'area',
        name: '數',
        code: 'NUM',
        description: '數字、數量與簡單運算。',
        children: [
          {
            id: 'unit-addition-10',
            level: 'unit',
            name: '10 以內加法',
            code: 'NUM-01',
            description: '10 以內加法應用。',
            children: [
              {
                id: 'category-addition-application',
                level: 'category',
                name: '加法應用',
                code: '21',
                description: '能運用 10 以內加法解決簡單生活情境。',
                children: []
              }
            ]
          },
          {
            id: 'unit-number-match',
            level: 'unit',
            name: '數字配對',
            code: 'NUM-02',
            description: '數字與數量的配對。',
            children: [
              {
                id: 'category-quantity-compare',
                level: 'category',
                name: '數量比較',
                code: '22',
                description: '能辨認數字與數量的配對關係。',
                children: []
              }
            ]
          }
        ]
      },
      {
        id: 'area-shape',
        level: 'area',
        name: '圖形與空間',
        code: 'SHAPE',
        description: '平面圖形與空間概念。',
        children: [
          {
            id: 'unit-plane-shapes',
            level: 'unit',
            name: '平面圖形',
            code: 'SHAPE-01',
            description: '常見平面圖形。',
            children: [
              {
                id: 'category-triangle',
                level: 'category',
                name: '三角形',
                code: '31',
                description: '能辨認常見平面圖形。',
                children: []
              },
              {
                id: 'category-plane-shapes',
                level: 'category',
                name: '平面圖形',
                code: '32',
                description: '能辨認圓形、三角形、正方形及長方形。',
                children: []
              }
            ]
          }
        ]
      },
      {
        id: 'area-measure',
        level: 'area',
        name: '度量',
        code: 'MEASURE',
        description: '以比較方式理解量度差異。',
        children: []
      },
      {
        id: 'area-logic-relation',
        level: 'area',
        name: '邏輯與關係',
        code: 'LOGIC',
        description: '分類、排序與關係辨識。',
        children: []
      },
      {
        id: 'area-logic-thinking',
        level: 'area',
        name: '邏輯思維',
        code: 'THINK',
        description: '規律與序列。',
        children: []
      },
      {
        id: 'area-data',
        level: 'area',
        name: '數據處理',
        code: 'DATA',
        description: '簡單資料整理與描述。',
        children: []
      }
    ]
  },
  chineseTaxonomySubject,
  {
    id: 'subject-english',
    level: 'subject',
    name: 'eng',
    code: 'ENG',
    description: 'English learning objective taxonomy.',
    children: [
      {
        id: 'area-phonics',
        level: 'area',
        name: 'Phonics',
        code: 'ENG-01',
        description: 'Letter sound recognition.',
        children: [
          {
            id: 'unit-letter-sounds',
            level: 'unit',
            name: 'Letter Sounds',
            code: 'ENG-01-01',
            description: 'Common letter sounds.',
            children: [
              {
                id: 'category-recognition',
                level: 'category',
                name: 'Recognition',
                code: '51',
                description: 'Recognise common letter sounds and match them to simple words.',
                children: []
              }
            ]
          }
        ]
      }
    ]
  }
];

function makeId(level: TaxonomyLevel) {
  return `${level}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function normalizeNode(node: Partial<TaxonomyNode>, level: TaxonomyLevel): TaxonomyNode {
  return {
    id: String(node.id || makeId(level)),
    level,
    name: String(node.name || '未命名'),
    code: String(node.code || ''),
    description: String(node.description || ''),
    children: Array.isArray(node.children)
      ? node.children.map((child) => normalizeNode(child, child.level || nextLevel(level)))
      : []
  };
}

function replaceChineseTaxonomy(subjects: TaxonomySubject[]): TaxonomySubject[] {
  const index = subjects.findIndex(
    (subject) =>
      subject.id === chineseTaxonomySubject.id ||
      subject.code === chineseTaxonomySubject.code ||
      subject.name === chineseTaxonomySubject.name
  );
  if (index < 0) return [...subjects, chineseTaxonomySubject];

  const next = [...subjects];
  next[index] = chineseTaxonomySubject;
  return next;
}

function nextLevel(level: TaxonomyLevel): TaxonomyLevel {
  if (level === 'subject') return 'area';
  if (level === 'area') return 'unit';
  return 'category';
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers
    }
  });

  if (!response.ok) {
    throw new Error(`Taxonomy mock API request failed: ${response.status}`);
  }

  return response.json();
}

export function getNextTaxonomyLevel(level: TaxonomyLevel): TaxonomyLevel | null {
  if (level === 'subject') return 'area';
  if (level === 'area') return 'unit';
  if (level === 'unit') return 'category';
  return null;
}

export function createTaxonomyNode(
  level: TaxonomyLevel,
  input: Pick<TaxonomyNode, 'name' | 'code' | 'description'>
): TaxonomyNode {
  return {
    id: makeId(level),
    level,
    name: input.name.trim(),
    code: input.code.trim(),
    description: input.description.trim(),
    children: []
  };
}

export function countTaxonomyLevels(subjects: TaxonomySubject[]) {
  const counts = {
    subjects: subjects.length,
    areas: 0,
    units: 0,
    categories: 0
  };

  const walk = (node: TaxonomyNode) => {
    node.children.forEach((child) => {
      if (child.level === 'area') counts.areas += 1;
      if (child.level === 'unit') counts.units += 1;
      if (child.level === 'category') counts.categories += 1;
      walk(child);
    });
  };

  subjects.forEach(walk);
  return counts;
}

export function taxonomyToObjectiveCascade(subjects: TaxonomySubject[]): ObjectiveCascade {
  return subjects.reduce<ObjectiveCascade>((subjectMap, subject) => {
    subjectMap[subject.name] = subject.children.reduce<ObjectiveCascade[string]>(
      (areaMap, area) => {
        areaMap[area.name] = area.children.reduce<ObjectiveCascade[string][string]>(
          (unitMap, unit) => {
            unitMap[unit.name] = unit.children.reduce<ObjectiveCascade[string][string][string]>(
              (categoryMap, category) => {
                // Category description IS the evaluation indicator. The old
                // `category.children[0]?.description` fallback referenced the
                // dropped `item` leaf; keep `unit.description` as a final
                // fallback so legacy seed data still renders something.
                categoryMap[category.name] = category.description || unit.description;
                return categoryMap;
              },
              {}
            );
            return unitMap;
          },
          {}
        );
        return areaMap;
      },
      {}
    );
    return subjectMap;
  }, {});
}

export function findTaxonomyNode(subjects: TaxonomySubject[], id: string): TaxonomyNode | null {
  const stack: TaxonomyNode[] = [...subjects];

  while (stack.length) {
    const node = stack.shift();
    if (!node) continue;
    if (node.id === id) return node;
    stack.push(...node.children);
  }

  return null;
}

export function upsertTaxonomyNode(
  subjects: TaxonomySubject[],
  parentId: string | null,
  node: TaxonomyNode
) {
  if (!parentId && node.level === 'subject') {
    return [...subjects, node as TaxonomySubject];
  }

  const append = (items: TaxonomyNode[]): TaxonomyNode[] =>
    items.map((item) =>
      item.id === parentId
        ? { ...item, children: [...item.children, node] }
        : { ...item, children: append(item.children) }
    );

  return append(subjects) as TaxonomySubject[];
}

export function updateTaxonomyNode(
  subjects: TaxonomySubject[],
  id: string,
  input: Pick<TaxonomyNode, 'name' | 'code' | 'description'>
) {
  const update = (items: TaxonomyNode[]): TaxonomyNode[] =>
    items.map((item) =>
      item.id === id ? { ...item, ...input } : { ...item, children: update(item.children) }
    );

  return update(subjects) as TaxonomySubject[];
}

export function removeTaxonomyNode(subjects: TaxonomySubject[], id: string) {
  const remove = (items: TaxonomyNode[]): TaxonomyNode[] =>
    items
      .filter((item) => item.id !== id)
      .map((item) => ({ ...item, children: remove(item.children) }));

  return remove(subjects) as TaxonomySubject[];
}

export const taxonomyClient = {
  async list() {
    try {
      const subjects = await request<Partial<TaxonomySubject>[]>(TAXONOMY_SUBJECTS_URL);
      return replaceChineseTaxonomy(
        subjects.map((subject) => normalizeNode(subject, 'subject') as TaxonomySubject)
      );
    } catch (error) {
      return defaultTaxonomySubjects;
    }
  },

  async replaceAll(subjects: TaxonomySubject[]) {
    const existing = await this.list();
    await Promise.all(
      existing.map((subject) =>
        fetch(`${TAXONOMY_SUBJECTS_URL}/${subject.id}`, {
          method: 'DELETE'
        })
      )
    );
    await Promise.all(
      subjects.map((subject) =>
        request<TaxonomySubject>(TAXONOMY_SUBJECTS_URL, {
          method: 'POST',
          body: JSON.stringify(subject)
        })
      )
    );
    return subjects;
  },

  // Granular CRUD mirroring the Henry client interface, implemented over the
  // mock's bulk replaceAll. Mock keys nodes by `id`; Henry keys by `code`.
  async addNode(
    level: TaxonomyLevel,
    input: { name: string; description?: string },
    parent: TaxonomyNode | null
  ): Promise<void> {
    const subjects = await this.list();
    const node = createTaxonomyNode(level, {
      name: input.name,
      code: '',
      description: input.description || ''
    });
    await this.replaceAll(upsertTaxonomyNode(subjects, parent ? parent.id : null, node));
  },

  async updateNode(
    node: TaxonomyNode,
    input: { name: string; description?: string }
  ): Promise<void> {
    const subjects = await this.list();
    await this.replaceAll(
      updateTaxonomyNode(subjects, node.id, {
        name: input.name,
        code: node.code,
        description: input.description || ''
      })
    );
  },

  async deleteNode(node: TaxonomyNode): Promise<void> {
    const subjects = await this.list();
    await this.replaceAll(removeTaxonomyNode(subjects, node.id));
  }
};
